import {
	Crm,
	CrmScope,
	ICrmTemplateParamValue,
	INotification,
} from '@phonepe/crm';
import { partition } from 'lodash';

import {
	IActionType,
	IMessageActionType,
	IMessageData,
	IMessageType,
	INavParamsData,
	NotificationsHandler,
} from '@/types';
import Api from '@/data';

import { dayjs } from './dateTimeUtils';
import { getUrlWithParams } from './queryUtils';

const CRM_ID = 'pfb-web-crm';

export const getMessageNavType = (paramsData: INavParamsData) => {
	const redirectionData = paramsData?.redirection_data?.data;
	if (!redirectionData) return {};

	return redirectionData.reduce((acc: IActionType, redirectionData) => {
		if (redirectionData.key === 'type') {
			acc.type = redirectionData.value as IMessageType;
		}
		if (redirectionData.key === 'actionType') {
			acc.actionType = redirectionData.value as IMessageActionType;
		}
		return acc;
	}, {});
};

export function getParseNotifications(
	notifications: INotification[],
): IMessageData[] {
	return notifications.reduce<IMessageData[]>(
		(parsedNotifications, { id, templateParams, sentAt, nav }) => {
			if (!templateParams?.value) return parsedNotifications;

			const { iconURL, title, subTitle, message } =
				templateParams.value as ICrmTemplateParamValue;

			parsedNotifications.push({
				id,
				icon: getUrlWithParams(iconURL, { width: 24, height: 24 }),
				title,
				subTitle,
				message,
				date: dayjs(sentAt).format('MMM DD, YYYY hh:mm A'),
				link: nav?.webDeeplink,
				...getMessageNavType(nav?.params as INavParamsData),
			});

			return parsedNotifications;
		},
		[],
	);
}

export default class NotificationManager {
	private static _isInitialized = false;
	private static readonly _scope = [CrmScope.INBOX];
	private static _crmClient: Crm;
	private static _notificationsHandler?: NotificationsHandler;

	static readonly reset = () => {
		if (NotificationManager._isInitialized) {
			NotificationManager._isInitialized = false;
			Crm.cleanupById(CRM_ID);
		}
	};

	static readonly setNotificationsHandler = (
		notificationsHandler: NotificationsHandler,
	) => {
		NotificationManager._notificationsHandler = notificationsHandler;
	};

	static readonly init = async (merchantUserId: string) => {
		if (NotificationManager._isInitialized) {
			return;
		}

		NotificationManager._isInitialized = true;

		const crmClient = new Crm({
			bullhorn: {
				apiConfig: {
					fetcher: async (config) => {
						const response = await Api.bullhornRequest(config);
						return response.data;
					},
				},
				polling: {
					interval: 3 * 60 * 1000, // 3 minutes
				},
				merchantUserId,
			},
			scopes: NotificationManager._scope,
			id: CRM_ID,
			persist: {
				maxRead: 10,
				persistScopes: [CrmScope.INBOX],
			},
			onNotificationsChange: ({ allNotifications, newNotifications }) => {
				const [read = [], unread = []] = partition(
					allNotifications[CrmScope.INBOX],
					(notification) => notification.hasRead,
				);

				NotificationManager._notificationsHandler?.({
					read: getParseNotifications(read),
					unread: getParseNotifications(unread),
					newNotifications: getParseNotifications(
						newNotifications[CrmScope.INBOX] ?? [],
					),
				});
			},
		});

		crmClient.init();
		NotificationManager._crmClient = crmClient;
	};

	static readonly markAllRead = () => {
		NotificationManager._crmClient?.markNotificationsAsRead('*');
	};
}
